// Service for the BPR Redesign endpoints (/cms/bpr-redesign/*) — a third
// redesign pipeline alongside /cms/optimize (processOptimizationService.js)
// and /cms/rl-optimize (rlOptimizationService.js). Unlike those two, the
// redesign decision is made by an external, already-trained ML model: there
// is no goal/episodes/seed to pass and no background-job/poll variant — the
// POST below returns the full result directly. See FRONTEND_GUIDE.md.
import { aiFetch } from "./aiServiceAuth";
const BASE_URL =
  process.env.REACT_APP_PROCESS_OPT_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  "";

function buildHeaders(extra = {}) {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...extra,
  };
}

function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return aiFetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === "AbortError") throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
      throw err;
    });
}

// `detail` is a plain string for 404/500 and an object ({error, message,
// upstream_detail}) for 400/502 (§6 of FRONTEND_GUIDE.md) — normalize both
// into one Error shape, carrying the upstream model's own message when present.
async function throwApiError(res) {
  let body = null;
  try { body = await res.json(); } catch (_) { }
  const detail = body?.detail;
  const message = typeof detail === "string" ? detail : (detail?.message || `HTTP ${res.status}`);
  const err = new Error(message);
  err.status = res.status;
  err.errorCode = (detail && typeof detail === "object" && detail.error) || null;
  err.upstreamDetail = (detail && typeof detail === "object" && detail.upstream_detail) || null;
  throw err;
}

async function getJson(path, timeoutMs = 30_000) {
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, { method: "GET", headers: buildHeaders() }, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json();
}

async function postJson(path, timeoutMs = 90_000) {
  const res = await fetchWithTimeout(
    `${BASE_URL}${path}`,
    { method: "POST", headers: buildHeaders(), body: JSON.stringify({}) },
    timeoutMs,
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

async function postJsonWithParams(path, params, timeoutMs = 90_000) {
  const qs = params.toString();
  return postJson(`${path}${qs ? `?${qs}` : ""}`, timeoutMs);
}

// Is the external BPR Redesign model reachable right now? Cheap diagnostic
// call, not required before running a redesign.
export async function getBprHealth() {
  return getJson("/cms/bpr-redesign/health", 10_000);
}

// The 10 heuristic names the external model can apply.
export async function getBprHeuristics() {
  return getJson("/cms/bpr-redesign/heuristics", 15_000);
}

// One call, one result — no job to poll. Almost nothing to configure: the only
// knob is `includeSubprocessRedesigns`, a query param (unlike RL's, which is a body
// field) that also redesigns every sub-process and rolls the result into the parent
// — one extra upstream call per sub-process plus one roll-up call, so the timeout is
// raised to match when it's on.
export async function getBprRedesign(processId, { includeSubprocessRedesigns } = {}) {
  if (!processId) throw new Error("processId is required");
  const params = new URLSearchParams();
  if (includeSubprocessRedesigns) params.set("include_subprocess_redesigns", "true");
  return postJsonWithParams(
    `/cms/bpr-redesign/${encodeURIComponent(processId)}`,
    params,
    includeSubprocessRedesigns ? 180_000 : 90_000,
  );
}

// The endpoint now returns two top-level, ready-to-render BPMN 2.0 + BPMNDI
// XML strings — `bpmn_xml` (TO-BE, every applied heuristic already reflected
// in the diagram shape) and `as_is_bpmn_xml` (the original, for a side-by-side
// view) — plus `bpmn_xml_source`: "stored" | "generated" | "missing". When
// it's "missing" both XML fields are null; that's an expected, harmless case
// (not every process has a stored diagram), never an error. The old nested
// to_be.bpmn_xml/as_is.bpmn_xml fallbacks are kept for any response that
// predates the top-level fields.
export function extractBprBpmnXml(result) {
  if (!result) return null;
  return (
    result.bpmn_xml ||
    result.to_be?.bpmn_xml ||
    result.as_is?.bpmn_xml ||
    null
  ) || null;
}

export function extractBprAsIsBpmnXml(result) {
  if (!result) return null;
  return (result.as_is_bpmn_xml || result.as_is?.bpmn_xml || null) || null;
}

export function getBprBpmnSource(result) {
  return result?.bpmn_xml_source || null;
}

// Every redesign response now carries a `result_ref` — the handle
// /save-optimized-version takes as ?bpr_result_ref to persist exactly THIS
// result rather than calling the external model again (which would both cost a
// round trip and risk saving a redesign the analyst never reviewed).
//
// The backend retains the 64 most recent results for one hour. Past that the
// reference is gone and the save re-runs the engine, which is why the save
// response's redesign_provenance reports result_reused/reuse_note — expiry is a
// normal outcome to surface, not an error to swallow.
export function extractBprResultRef(result) {
  return result?.result_ref || null;
}
